/**
 * BulkUploadPanel - Bulk User Role Assignment Upload
 *
 * FERP CRITICAL: Bulk CSV upload for role assignments with validation
 *
 * Features:
 * - Drag-and-drop CSV file upload
 * - File size validation (max 10MB)
 * - CSV preview (first 10 rows)
 * - Processing mode selection
 * - Error threshold configuration
 * - Template download
 * - Complete validation before upload
 *
 * Architecture Principles:
 * - Data-driven (no hardcoded business logic)
 * - Repository pattern (BulkOperationRepository)
 * - File validation (size, format, structure)
 * - Immutable audit trails
 */

import React, { useState, useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Table,
  Button,
  Select,
  NumberInput,
  Alert,
  Badge,
  Group,
  Card,
  List,
  Box,
} from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import {
  IconUpload,
  IconX,
  IconFile,
  IconDownload,
  IconAlertCircle,
} from '@tabler/icons-react';
import Papa from 'papaparse';
import { ProcessingModeInput } from '../data/repositories/BulkOperationRepository';

export interface BulkUploadPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Callback when CSV is uploaded and ready to process */
  onUpload: (file: File, csvContent: string, processingMode: ProcessingModeInput) => void;
  /** Is currently processing */
  isProcessing?: boolean;
}

interface CsvRow {
  user_id: string;
  role_name: string;
  operation: string;
  expiration_date?: string;
  reason?: string;
}

/**
 * Bulk Upload Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (no hardcoded business logic)
 * - ✅ Repository pattern (operations through BulkOperationRepository)
 * - ✅ File validation (size, format, structure)
 * - ✅ Immutable audit trails
 */
export function BulkUploadPanel({
  tenantId,
  onUpload,
  isProcessing = false,
}: BulkUploadPanelProps) {
  // State
  const [file, setFile] = useState<FileWithPath | null>(null);
  const [csvContent, setCsvContent] = useState<string>('');
  const [preview, setPreview] = useState<CsvRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [mode, setMode] = useState<'stop_on_error' | 'continue_on_error' | 'dry_run'>('continue_on_error');
  const [threshold, setThreshold] = useState<number>(10); // 10%

  /**
   * Handle file drop
   */
  const handleDrop = useCallback(async (files: FileWithPath[]) => {
    const uploadedFile = files[0];
    if (!uploadedFile) return;
    const validationErrors: string[] = [];

    // Validate file size (max 10MB)
    if (uploadedFile.size > 10 * 1024 * 1024) {
      validationErrors.push('File size exceeds 10MB limit');
      setErrors(validationErrors);
      return;
    }

    // Validate file type
    if (!uploadedFile.name.endsWith('.csv')) {
      validationErrors.push('File must be a CSV file (.csv extension)');
      setErrors(validationErrors);
      return;
    }

    try {
      // Read file content
      const content = await uploadedFile.text();
      setCsvContent(content);

      // Parse CSV
      const parseResult = Papa.parse<CsvRow>(content, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().toLowerCase(),
      });

      // Validate CSV structure
      const requiredColumns = ['user_id', 'role_name', 'operation'];
      const headers = Object.keys(parseResult.data[0] || {});
      const missingColumns = requiredColumns.filter(col => !headers.includes(col));

      if (missingColumns.length > 0) {
        validationErrors.push(`Missing required columns: ${missingColumns.join(', ')}`);
        setErrors(validationErrors);
        return;
      }

      // Validate row count (max 10,000)
      if (parseResult.data.length > 10000) {
        validationErrors.push(`CSV exceeds 10,000 row limit (found ${parseResult.data.length} rows)`);
        setErrors(validationErrors);
        return;
      }

      // Validate data
      parseResult.data.forEach((row, idx) => {
        const rowNum = idx + 2; // +2 for header row + 0-index

        if (!row.user_id || row.user_id.trim() === '') {
          validationErrors.push(`Row ${rowNum}: Missing user_id`);
        }
        if (!row.role_name || row.role_name.trim() === '') {
          validationErrors.push(`Row ${rowNum}: Missing role_name`);
        }
        if (!row.operation || row.operation.trim() === '') {
          validationErrors.push(`Row ${rowNum}: Missing operation`);
        }
        if (row.operation && !['assign', 'revoke', 'update_expiration'].includes(row.operation.toLowerCase())) {
          validationErrors.push(`Row ${rowNum}: Invalid operation "${row.operation}" (must be assign, revoke, or update_expiration)`);
        }
        if (row.operation?.toLowerCase() === 'revoke' && (!row.reason || row.reason.trim() === '')) {
          validationErrors.push(`Row ${rowNum}: Revoke operation requires reason`);
        }
      });

      if (validationErrors.length > 0) {
        setErrors(validationErrors.slice(0, 10)); // Show first 10 errors
        setFile(null);
        setPreview([]);
        return;
      }

      // Success - set preview (first 10 rows)
      setFile(uploadedFile);
      setPreview(parseResult.data.slice(0, 10));
      setErrors([]);
    } catch (error) {
      console.error('[BulkUploadPanel] Failed to parse CSV:', error);
      validationErrors.push('Failed to parse CSV file. Please check format.');
      setErrors(validationErrors);
    }
  }, []);

  /**
   * Handle upload button click
   */
  const handleUploadClick = useCallback(() => {
    if (!file || !csvContent) return;

    const processingMode: ProcessingModeInput = {
      modeType: mode,
      errorThreshold: mode === 'continue_on_error' ? threshold / 100 : undefined,
    };

    onUpload(file, csvContent, processingMode);
  }, [file, csvContent, mode, threshold, onUpload]);

  /**
   * Download CSV template
   */
  const downloadTemplate = useCallback(() => {
    const template = [
      'user_id,role_name,operation,expiration_date,reason',
      'alice_123,admin,assign,2026-01-01T00:00:00Z,Promotion to team lead',
      'bob_456,viewer,assign,,New team member',
      'charlie_789,analyst,revoke,,Left organization',
      'david_012,approver,assign,2025-12-31T23:59:59Z,Temporary approver for Q4',
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_assignment_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Stack gap="md">
      <Group justify="space-between" ta="center">
        <Title order={3}>Bulk User Assignment</Title>
        <Button
          variant="subtle"
          leftSection={<IconDownload size={16} />}
          onClick={downloadTemplate}
        >
          Download CSV Template
        </Button>
      </Group>

      <Card withBorder>
        <Dropzone
          onDrop={handleDrop}
          accept={['text/csv']}
          maxSize={10 * 1024 * 1024}
          disabled={isProcessing}
        >
          <Group justify="center" gap="xl" style={{ minHeight: 120, pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={50} stroke={1.5} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={50} stroke={1.5} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFile size={50} stroke={1.5} />
            </Dropzone.Idle>

            <div>
              <Text size="xl" inline>
                Drag CSV file here or click to select
              </Text>
              <Text size="sm" c="dimmed" inline mt={7}>
                Max 10MB, 10,000 rows
              </Text>
            </div>
          </Group>
        </Dropzone>
      </Card>

      {errors.length > 0 && (
        <Alert
          color="red"
          title="Validation Errors"
          icon={<IconAlertCircle />}
        >
          <List>
            {errors.map((error, idx) => (
              <List.Item key={idx}>{error}</List.Item>
            ))}
          </List>
          {errors.length === 10 && (
            <Text size="sm" c="dimmed" mt="xs">
              Showing first 10 errors. Please fix these and re-upload.
            </Text>
          )}
        </Alert>
      )}

      {preview.length > 0 && (
        <>
          <Title order={4}>Preview (first 10 rows of {file?.name})</Title>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User ID</Table.Th>
                  <Table.Th>Role Name</Table.Th>
                  <Table.Th>Operation</Table.Th>
                  <Table.Th>Expiration</Table.Th>
                  <Table.Th>Reason</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.map((row, idx) => (
                  <Table.Tr key={idx}>
                    <Table.Td>{row.user_id}</Table.Td>
                    <Table.Td>{row.role_name}</Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          row.operation.toLowerCase() === 'assign'
                            ? 'green'
                            : row.operation.toLowerCase() === 'revoke'
                            ? 'red'
                            : 'blue'
                        }
                      >
                        {row.operation.toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{row.expiration_date || '—'}</Table.Td>
                    <Table.Td>{row.reason || '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>

          <Card withBorder>
            <Stack gap="md">
              <Title order={5}>Processing Options</Title>

              <Select
                label="Processing Mode"
                description="How to handle errors during processing"
                value={mode}
                onChange={(value) => setMode(value as typeof mode)}
                data={[
                  {
                    value: 'stop_on_error',
                    label: 'Stop on first error',
                  },
                  {
                    value: 'continue_on_error',
                    label: 'Continue on error (with threshold)',
                  },
                  {
                    value: 'dry_run',
                    label: 'Dry run (validate only, no changes)',
                  },
                ]}
              />

              {mode === 'continue_on_error' && (
                <NumberInput
                  label="Error Threshold (%)"
                  description="Stop if error rate exceeds this percentage"
                  value={threshold}
                  onChange={(val) => setThreshold(val as number)}
                  min={0}
                  max={100}
                  step={1}
                  suffix="%"
                />
              )}

              <Button
                onClick={handleUploadClick}
                disabled={isProcessing}
                loading={isProcessing}
                fullWidth
              >
                {mode === 'dry_run' ? 'Validate' : 'Process'} {preview.length} rows
              </Button>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

export default BulkUploadPanel;
